import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { CollectionEditor } from "@/components/admin/CollectionEditor";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { LogOut, Save, ExternalLink, Trash2, Mail } from "lucide-react";

const input = "w-full bg-[#0e0e0e] border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4FF00] focus:outline-none";
const label = "font-mono-t text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5 block";

const TABS = ["Site Content", "Services", "Projects", "Testimonials", "Messages"];

export default function Admin() {
  const { user, logout } = useAuth();
  const { content, refresh } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Site Content");
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (content?.settings) setSettings(structuredClone(content.settings));
  }, [content]);

  useEffect(() => {
    if (tab === "Messages") api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => {});
  }, [tab]);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const setS = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });
  const setChapter = (i, k, v) => {
    const ch = [...(settings.chapters || [])];
    ch[i] = { ...ch[i], [k]: v };
    setSettings({ ...settings, chapters: ch });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Site content saved");
      await refresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const deleteMessage = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    setMessages((m) => m.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  if (!settings) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">Loading…</div>;

  return (
    <div className="grain min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      {/* Topbar */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur-xl z-40">
        <Logo light />
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1.5"><ExternalLink size={15} /> View site</a>
          <span className="text-zinc-600">·</span>
          <span className="text-sm text-zinc-400 hidden sm:inline">{user?.email}</span>
          <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm border border-white/20 px-3 py-1.5 hover:border-white"><LogOut size={15} /> Logout</button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 border-b border-white/10 pb-4">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm px-4 py-2 font-medium transition-colors ${tab === t ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "Site Content" && (
          <div className="space-y-10" data-testid="settings-panel">
            <div>
              <h2 className="font-display text-2xl tracking-tight mb-6">Hero</h2>
              <div className="space-y-4">
                <div><label className={label}>Eyebrow</label><input className={input} value={settings.hero_eyebrow || ""} onChange={setS("hero_eyebrow")} data-testid="s-hero-eyebrow" /></div>
                <div><label className={label}>Title</label><textarea rows={2} className={input} value={settings.hero_title || ""} onChange={setS("hero_title")} data-testid="s-hero-title" /></div>
                <div><label className={label}>Subtitle</label><textarea rows={2} className={input} value={settings.hero_subtitle || ""} onChange={setS("hero_subtitle")} data-testid="s-hero-subtitle" /></div>
                <div><label className={label}>CTA Button Text</label><input className={input} value={settings.hero_cta || ""} onChange={setS("hero_cta")} /></div>
                <div><label className={label}>Marquee Text</label><input className={input} value={settings.marquee_text || ""} onChange={setS("marquee_text")} /></div>
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl tracking-tight mb-6">About / Manifesto</h2>
              <div><label className={label}>Intro</label><textarea rows={3} className={input} value={settings.about_intro || ""} onChange={setS("about_intro")} data-testid="s-about-intro" /></div>
              <div className="mt-6 space-y-4">
                {(settings.chapters || []).map((c, i) => (
                  <div key={i} className="border border-white/10 p-4 space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div><label className={label}>No.</label><input className={input} value={c.number} onChange={(e) => setChapter(i, "number", e.target.value)} /></div>
                      <div className="col-span-3"><label className={label}>Title</label><input className={input} value={c.title} onChange={(e) => setChapter(i, "title", e.target.value)} /></div>
                    </div>
                    <div><label className={label}>Body</label><textarea rows={2} className={input} value={c.body} onChange={(e) => setChapter(i, "body", e.target.value)} /></div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl tracking-tight mb-6">Contact & Social</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className={label}>Email</label><input className={input} value={settings.contact_email || ""} onChange={setS("contact_email")} /></div>
                <div><label className={label}>Phone</label><input className={input} value={settings.contact_phone || ""} onChange={setS("contact_phone")} /></div>
                <div><label className={label}>Address</label><input className={input} value={settings.contact_address || ""} onChange={setS("contact_address")} /></div>
                <div><label className={label}>Instagram URL</label><input className={input} value={settings.instagram || ""} onChange={setS("instagram")} /></div>
                <div><label className={label}>LinkedIn URL</label><input className={input} value={settings.linkedin || ""} onChange={setS("linkedin")} /></div>
              </div>
            </div>

            <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings" className="inline-flex items-center gap-2 bg-[#D4FF00] text-black font-semibold px-6 py-3 disabled:opacity-50">
              <Save size={17} /> {savingSettings ? "Saving…" : "Save site content"}
            </button>
          </div>
        )}

        {tab === "Services" && (
          <CollectionEditor title="Services" name="services" items={content?.services} refresh={refresh}
            fields={[
              { key: "title", label: "Title" },
              { key: "subtitle", label: "Subtitle" },
              { key: "description", label: "Description", type: "textarea" },
              { key: "tags", label: "Tags (comma separated)" },
              { key: "order", label: "Order", type: "number" },
            ]} />
        )}

        {tab === "Projects" && (
          <CollectionEditor title="Projects" name="projects" items={content?.projects} refresh={refresh}
            fields={[
              { key: "title", label: "Title" },
              { key: "category", label: "Category" },
              { key: "year", label: "Year" },
              { key: "image", label: "Image URL" },
              { key: "description", label: "Description", type: "textarea" },
              { key: "order", label: "Order", type: "number" },
            ]} />
        )}

        {tab === "Testimonials" && (
          <CollectionEditor title="Testimonials" name="testimonials" items={content?.testimonials} refresh={refresh}
            fields={[
              { key: "quote", label: "Quote", type: "textarea" },
              { key: "author", label: "Author" },
              { key: "company", label: "Company" },
              { key: "order", label: "Order", type: "number" },
            ]} />
        )}

        {tab === "Messages" && (
          <div data-testid="messages-panel">
            <h2 className="font-display text-2xl tracking-tight mb-6">Contact Messages</h2>
            {messages.length === 0 && <p className="text-zinc-500">No messages yet.</p>}
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="border border-white/10 bg-[#0e0e0e] p-4" data-testid={`message-${m.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium">{m.name} <span className="text-zinc-500 font-normal">· {m.company || "—"}</span></p>
                      <a href={`mailto:${m.email}`} className="text-sm text-[#D4FF00] inline-flex items-center gap-1.5"><Mail size={13} /> {m.email}</a>
                      <p className="text-zinc-300 mt-3 leading-relaxed">{m.message}</p>
                      <p className="text-xs text-zinc-600 mt-2">{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => deleteMessage(m.id)} className="text-[#FF3366] border border-[#FF3366]/40 p-2 shrink-0"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
